/**
 * AnnouncementDatesEditor — Wave 3 Batch J. Renders the legacy
 * Announcement detail page's "Choose Date Type" Time/Duration toggle +
 * multi-row date list with Add/Remove (legacy lines 620-867 of
 * `AnnouncementDetails.jsx`).
 *
 * Each date row carries the same shape as the BE's `AnnouncementDateRow`:
 *   - entryType: 'time' | 'duration'
 *   - date: 'YYYY-MM-DD'
 *   - When time:    startTime / endTime ('HH:MM')
 *   - When duration: hours / minutes
 *
 * The mode is GLOBAL across all rows on the announcement (legacy parity
 * — the radio at the top toggles every row). Switching mode preserves
 * the date column on each row but clears the time/duration fields.
 *
 * The editor is self-contained: it owns the draft state. The parent
 * provides initial rows + a `onSave` callback that takes the full new
 * list (atomic replace, mirrors the BE update endpoint). The parent
 * decides when to call `onSave` (typically from an EditableField
 * Save button).
 */

import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { AnnouncementDateRow } from '@/api/types-bridge'

interface DraftRow {
  /** Local key for React reconciliation. UUIDs from the BE when present, else generated. */
  key: string
  date: string
  startTime: string
  endTime: string
  hours: number | null
  minutes: number | null
}

interface AnnouncementDatesEditorProps {
  rows: readonly AnnouncementDateRow[]
  /** Called as the user edits — parent persists draft state for an
      explicit Save action. */
  onChange: (next: DateInputForApi[]) => void
  /**
   * `default` is the original wrapping row. `rail` stacks each row for the
   * narrow fields rail: full-width date, then Start | End (or Hours | Minutes)
   * in two columns, with the remove button in the row's top-end corner.
   */
  layout?: 'default' | 'rail'
}

/** What the BE update endpoint accepts. */
export interface DateInputForApi {
  entryType: 'time' | 'duration'
  date: string
  startTime?: string | null
  endTime?: string | null
  hours?: number | null
  minutes?: number | null
  position?: number
}

const HOUR_OPTIONS = Array.from({ length: 9 }, (_, i) => i) // 0..8 (legacy cap)
const MINUTE_OPTIONS = [0, 15, 30, 45]

const todayIso = () => {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const inputBase =
  'rounded border border-border-strong bg-bg-elevated px-2 py-1 text-sm text-fg focus:border-fg focus:outline-none focus:ring-1 focus:ring-fg'

/** Groups a Start | End (or Hours | Minutes) pair into two columns in the
    rail layout; in the default layout it adds no element at all, so the pair
    stays direct children of the wrapping row. Declared at module scope so its
    identity is stable and the inputs never remount while typing. */
function FieldPair({ rail, children }: { rail: boolean; children: ReactNode }) {
  return rail ? <div className="grid grid-cols-2 gap-2">{children}</div> : <>{children}</>
}

const newKey = () => `row-${Math.random().toString(36).slice(2, 10)}`

/** `dateIso` + 1 calendar day in date-string space; falls back to today
 * when the input doesn't parse (a half-typed date in the row above). */
const nextIsoDate = (dateIso: string): string => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateIso)) return todayIso()
  const [y, m, d] = dateIso.split('-').map(Number)
  const next = new Date(Date.UTC(y ?? 0, (m ?? 1) - 1, (d ?? 1) + 1))
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${next.getUTCFullYear()}-${pad(next.getUTCMonth() + 1)}-${pad(next.getUTCDate())}`
}

function rowsToDrafts(rows: readonly AnnouncementDateRow[]): DraftRow[] {
  return rows.map((r) => ({
    key: r.id,
    date: r.date,
    startTime: r.startTime ?? '',
    endTime: r.endTime ?? '',
    hours: r.hours,
    minutes: r.minutes,
  }))
}

function inferGlobalMode(rows: readonly AnnouncementDateRow[]): 'time' | 'duration' {
  // Legacy mode is global: take the first row's type, fallback to 'time'.
  return rows[0]?.entryType ?? 'time'
}

export function AnnouncementDatesEditor({
  rows,
  onChange,
  layout = 'default',
}: AnnouncementDatesEditorProps) {
  const rail = layout === 'rail'
  const rootRef = useRef<HTMLDivElement>(null)
  const addButtonRef = useRef<HTMLButtonElement>(null)
  // Index of the row to refocus after a removal. Removing a row unmounts the
  // focused Remove button, which would drop focus to the body, where the
  // wrapper's Escape handler never sees the key.
  const pendingFocusIndex = useRef<number | null>(null)
  // In the rail the editor opens from a click-to-edit field; without this a
  // keyboard user's focus drops to the document body when it mounts.
  useEffect(() => {
    if (!rail) return
    rootRef.current?.querySelector<HTMLInputElement>('input:checked, input')?.focus()
  }, [rail])
  const { t } = useTranslation('board')
  const [mode, setMode] = useState<'time' | 'duration'>(() => inferGlobalMode(rows))
  const [drafts, setDrafts] = useState<DraftRow[]>(() => rowsToDrafts(rows))

  // Re-seed when the parent passes in new rows (e.g. after a refresh).
  // Compare by row-id content rather than array reference — the
  // CardCreateModal passes a literal `rows={[]}` which creates a fresh
  // array every render, so a reference-based dep would wipe local
  // state on every parent re-render (defeating the whole component).
  const rowsKey = rows.map((r) => r.id).join('|')
  // biome-ignore lint/correctness/useExhaustiveDependencies: rowsKey is a stable content fingerprint of `rows`; intentionally omitting `rows` itself to avoid reference-driven re-seeding.
  useEffect(() => {
    setMode(inferGlobalMode(rows))
    setDrafts(rowsToDrafts(rows))
  }, [rowsKey])

  // Whenever drafts or mode change, push the API-shape list up.
  // Memoize to avoid unstable identity in dep arrays of consumers.
  const apiList = useMemo<DateInputForApi[]>(
    () =>
      drafts.map((d, i) => ({
        entryType: mode,
        date: d.date,
        startTime: mode === 'time' ? d.startTime || null : null,
        endTime: mode === 'time' ? d.endTime || null : null,
        hours: mode === 'duration' ? d.hours : null,
        minutes: mode === 'duration' ? d.minutes : null,
        position: i,
      })),
    [drafts, mode],
  )

  useEffect(() => {
    onChange(apiList)
  }, [apiList, onChange])

  const updateDraft = (key: string, patch: Partial<DraftRow>) => {
    setDrafts((current) => current.map((d) => (d.key === key ? { ...d, ...patch } : d)))
  }
  const removeDraft = (key: string) => {
    pendingFocusIndex.current = Math.max(
      0,
      Math.min(
        drafts.findIndex((d) => d.key === key),
        drafts.length - 2,
      ),
    )
    setDrafts((current) => current.filter((d) => d.key !== key))
  }
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs after each drafts change; the ref carries the intent.
  useEffect(() => {
    const index = pendingFocusIndex.current
    if (index === null) return
    pendingFocusIndex.current = null
    const rowEls = rootRef.current?.querySelectorAll<HTMLElement>('[data-draft-row]')
    const target = rowEls?.[index]?.querySelector<HTMLElement>('input, select')
    if (target) target.focus()
    else addButtonRef.current?.focus()
  }, [drafts])
  const addDraft = () => {
    setDrafts((current) => {
      // Seed the new row from the one above it: the day AFTER its date
      // (legacy behavior — adding a row means "and the next day too"),
      // carrying its times over so a multi-day 9–5 window doesn't need
      // per-row re-entry. First row of an empty list starts at today.
      const last = current[current.length - 1]
      return [
        ...current,
        {
          key: newKey(),
          date: last ? nextIsoDate(last.date) : todayIso(),
          startTime: last?.startTime ?? '',
          endTime: last?.endTime ?? '',
          hours: last?.hours ?? 8,
          minutes: last?.minutes ?? 0,
        },
      ]
    })
  }

  return (
    <div ref={rootRef} className="flex flex-col gap-3">
      <div
        className={rail ? 'flex flex-wrap items-center gap-x-4 gap-y-1' : 'flex items-center gap-4'}
      >
        <span className="text-sm font-medium text-fg">{t('announcementDates.dateType')}</span>
        <label className="inline-flex items-center gap-1.5 text-sm text-fg">
          <input
            type="radio"
            name="announcement-date-mode"
            value="time"
            checked={mode === 'time'}
            onChange={() => setMode('time')}
          />
          {t('announcementDates.modeTime')}
        </label>
        <label className="inline-flex items-center gap-1.5 text-sm text-fg">
          <input
            type="radio"
            name="announcement-date-mode"
            value="duration"
            checked={mode === 'duration'}
            onChange={() => setMode('duration')}
          />
          {t('announcementDates.modeDuration')}
        </label>
      </div>

      <div className="flex flex-col gap-2">
        {drafts.length === 0 && (
          <p className="text-sm text-fg-muted">{t('announcementDates.empty')}</p>
        )}
        {drafts.map((d, idx) => (
          <div
            key={d.key}
            data-draft-row=""
            className={
              rail
                ? 'relative flex flex-col gap-2 rounded-md border border-border bg-bg-muted p-2'
                : 'flex flex-wrap items-end gap-2 rounded-md border border-border bg-bg-muted p-2'
            }
          >
            <label
              className={`flex flex-col gap-1 text-xs text-fg-muted${
                rail && drafts.length > 1 ? ' pe-9' : ''
              }`}
            >
              {t('announcementDates.date')}
              <input
                type="date"
                value={d.date}
                onChange={(e) => updateDraft(d.key, { date: e.target.value })}
                className={`${inputBase}${rail ? ' w-full min-w-0' : ''}`}
              />
            </label>
            {mode === 'time' && (
              <FieldPair rail={rail}>
                <label className="flex flex-col gap-1 text-xs text-fg-muted">
                  {t('announcementDates.start')}
                  <input
                    type="time"
                    value={d.startTime}
                    onChange={(e) => updateDraft(d.key, { startTime: e.target.value })}
                    className={`${inputBase}${rail ? ' w-full min-w-0' : ''}`}
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs text-fg-muted">
                  {t('announcementDates.end')}
                  <input
                    type="time"
                    value={d.endTime}
                    onChange={(e) => updateDraft(d.key, { endTime: e.target.value })}
                    className={`${inputBase}${rail ? ' w-full min-w-0' : ''}`}
                  />
                </label>
              </FieldPair>
            )}
            {mode === 'duration' && (
              <FieldPair rail={rail}>
                <label className="flex flex-col gap-1 text-xs text-fg-muted">
                  {t('announcementDates.hours')}
                  <select
                    value={d.hours ?? 0}
                    onChange={(e) => updateDraft(d.key, { hours: Number(e.target.value) })}
                    className={`${inputBase}${rail ? ' w-full min-w-0' : ''}`}
                  >
                    {HOUR_OPTIONS.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-xs text-fg-muted">
                  {t('announcementDates.minutes')}
                  <select
                    value={d.minutes ?? 0}
                    onChange={(e) => updateDraft(d.key, { minutes: Number(e.target.value) })}
                    disabled={d.hours === 8}
                    className={`${inputBase} disabled:bg-bg-muted disabled:text-fg-muted${rail ? ' w-full min-w-0' : ''}`}
                  >
                    {MINUTE_OPTIONS.map((m) => (
                      <option key={m} value={m} disabled={d.hours === 8 && m !== 0}>
                        {String(m).padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                </label>
              </FieldPair>
            )}
            {drafts.length > 1 && (
              <button
                type="button"
                onClick={() => removeDraft(d.key)}
                aria-label={t('announcementDates.removeRow', { index: idx + 1 })}
                className={
                  rail
                    ? 'absolute end-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-bg-muted text-fg hover:bg-border'
                    : 'ms-auto inline-flex h-7 w-7 items-center justify-center rounded-full bg-bg-muted text-fg hover:bg-slate-300'
                }
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <title>{t('announcementDates.remove')}</title>
                  <line x1="6" y1="6" x2="18" y2="18" />
                  <line x1="18" y1="6" x2="6" y2="18" />
                </svg>
              </button>
            )}
          </div>
        ))}
      </div>

      <button
        ref={addButtonRef}
        type="button"
        onClick={addDraft}
        className="inline-flex w-fit items-center gap-1.5 rounded-md border border-border-strong bg-bg-elevated px-3 py-1.5 text-sm text-fg shadow-sm hover:bg-bg-muted"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <title>{t('announcementDates.addIconTitle')}</title>
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
        {t('announcementDates.addAnother')}
      </button>
    </div>
  )
}
